import React, { createContext, forwardRef, useContext } from 'react';
import classNames from 'classnames';
import { CloseIcon } from 'components/icons';
import IconButton from 'components/shared/IconButton';
import Modal from 'components/shared/Modal';
import styles from './Dialog.module.css';

const DialogContext = createContext(null);

const Dialog = forwardRef(function Dialog({ open, hideBackdrop, onClose, className, children, ...rest }, forwardedRef) {
  return (
    <DialogContext.Provider value={{ onClose }}>
      <Modal open={open} hideBackdrop={hideBackdrop} onClose={onClose}>
        <div ref={forwardedRef} role="dialog" className={classNames(styles.root, className)} {...rest}>
          {children}
        </div>
      </Modal>
    </DialogContext.Provider>
  );
});

function DialogContainer({ as: Component = 'div', maxWidth, maxHeight, className, style, children, ...rest }) {
  return (
    <Component
      className={classNames(styles.container, className)}
      style={{
        maxWidth: toSize(maxWidth),
        maxHeight: toSize(maxHeight),
        ...style
      }}
      {...rest}
    >
      {children}
    </Component>
  );
}

Dialog.Container = DialogContainer;

Dialog.Title = function DialogHeader({ className, children, ...rest }) {
  return (
    <div className={classNames(styles.title, className)} {...rest}>
      {children}
    </div>
  );
};

Dialog.Content = function DialogContent({ className, children, ...rest }) {
  return (
    <div className={classNames(styles.content, styles.scrollbar, className)} {...rest}>
      {children}
    </div>
  );
};

Dialog.Actions = function DialogFooter({ className, children, ...rest }) {
  return (
    <div className={classNames(styles.actions, className)} {...rest}>
      {children}
    </div>
  );
};

Dialog.Close = function DialogClose({ className, onClick, ...rest }) {
  const dialog = useContext(DialogContext);

  const handleClick = (event) => {
    if (onClick) {
      onClick(event);
    }

    dialog.onClose(event);
  };

  return (
    <IconButton
      aria-label="Close"
      variant="text"
      color="secondary"
      size="x-small"
      className={classNames(styles.close, className)}
      onClick={handleClick}
      {...rest}
    >
      <CloseIcon />
    </IconButton>
  );
};

export default Dialog;

function toSize(value) {
  return typeof value === 'number' ? `${value}px` : value;
}
